import { Fragment } from 'react';
import { Calendar, Clock, DollarSign, Users } from 'lucide-react';

import type { RoiReworkFormula as RoiReworkFormulaModel } from '../types/roi';
import { formatCompactCurrency, formatCurrency } from '../utils/format';

interface RoiReworkFormulaProps {
  formula: RoiReworkFormulaModel;
}

interface FormulaTerm {
  icon: typeof Clock;
  label: string;
  primary: string;
}

export function RoiReworkFormula({ formula }: RoiReworkFormulaProps) {
  const terms: FormulaTerm[] = [
    {
      icon: Clock,
      label: 'Rework hours / engineer / month',
      primary: `${formula.hoursPerPersonPerMonth.toFixed(1)}h`,
    },
    {
      icon: Users,
      label: 'Engineers',
      primary: formula.engineeringHeadcount.toLocaleString('en-US'),
    },
    {
      icon: DollarSign,
      label: 'Loaded cost / hour',
      primary: formatCurrency(formula.ratePerEngineerHourUsd),
    },
    {
      icon: Calendar,
      label: 'Months / year',
      primary: `${formula.monthsPerYear}`,
    },
  ];

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_0_rgba(15,23,42,0.04)]">
      <header className="border-b border-gray-100 px-5 py-3">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
          How the rework recovery is calculated
        </span>
        <p className="mt-0.5 text-[11px] text-slate-500">
          Rework hours only - excludes churn, lost revenue, and bug-fix cost.
        </p>
      </header>

      <div className="grid grid-cols-2 items-stretch gap-3 px-5 py-4 md:grid-cols-9 md:gap-2">
        {terms.map((term, index) => (
          <Fragment key={term.label}>
            <FormulaCircle
              icon={term.icon}
              label={term.label}
              primary={term.primary}
            />
            {index < terms.length - 1 && <FormulaOperator symbol="×" />}
          </Fragment>
        ))}
        <FormulaOperator symbol="=" />
        <ResultCircle totalUsd={formula.totalUsd} />
      </div>
    </section>
  );
}

interface FormulaCircleProps {
  icon: typeof Clock;
  label: string;
  primary: string;
}

function FormulaCircle({ icon: Icon, label, primary }: FormulaCircleProps) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-1.5 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">
        <Icon className="h-4 w-4 text-slate-500" />
      </div>
      <div className="text-base font-bold tabular-nums text-slate-900">
        {primary}
      </div>
      <div className="mt-0.5 max-w-[120px] text-[10px] font-medium text-slate-500">
        {label}
      </div>
    </div>
  );
}

interface FormulaOperatorProps {
  symbol: string;
}

function FormulaOperator({ symbol }: FormulaOperatorProps) {
  return (
    <div className="flex items-center justify-center text-lg font-bold text-slate-300">
      {symbol}
    </div>
  );
}

interface ResultCircleProps {
  totalUsd: number;
}

function ResultCircle({ totalUsd }: ResultCircleProps) {
  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-1.5 flex h-10 w-10 items-center justify-center rounded-full bg-red-600 shadow-md">
        <DollarSign className="h-4 w-4 text-white" />
      </div>
      <div className="text-lg font-bold tabular-nums text-red-600">
        {formatCompactCurrency(totalUsd)}
      </div>
      <div className="mt-0.5 max-w-[120px] text-[10px] font-medium text-red-600">
        captured / year
      </div>
    </div>
  );
}
